import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Handshake, BadgeCheck, Percent, Gift } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PERKS = [
  { icon: Gift, title: "First booking is free", text: "No fees at all on the first package of yours that a member books." },
  { icon: Percent, title: "15% after that", text: "5% booking fee + 5% commission + 5% service share, taken from the trip cost. You receive 85%." },
  { icon: BadgeCheck, title: "Funded travelers", text: "Every booking comes from a member whose trip is already paid for by the club." },
];

const FIELDS = [
  ["name", "Your name"], ["email", "Work email", "email"], ["password", "Password (6+ characters)", "password"],
  ["company", "Company name"], ["license_number", "Seller-of-travel / agency license or registration #"],
  ["website", "Website"], ["insurance", "Insurance (E&O / liability carrier & limit)"], ["phone", "Phone"],
];

export default function PartnersPage() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState(Object.fromEntries(FIELDS.map(([k]) => [k, ""])));
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/brokers/apply", { ...f, accept_terms: agree });
      setUser(data.user);
      toast.success("Application received — we'll review it shortly");
      nav("/broker");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="partners-page" className="max-w-7xl mx-auto px-5 md:px-10 py-16 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-6">
        <p className="eyebrow"><Handshake className="w-3.5 h-3.5 mr-1.5" />Partner program</p>
        <h1 className="font-serif-lux text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">List your trips with <span className="text-gradient">VoyageQueue.</span></h1>
        <p className="text-slate-300 mt-6 text-base">Travel agencies, brokers and tour operators can submit vacation, concert and experience packages. Members vote on them, and when a member wins or buys a trip, you get the booking.</p>
        <div className="mt-10 space-y-4">
          {PERKS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="lux-card p-5 flex gap-4">
              <Icon className="w-6 h-6 text-[#38BDF8] shrink-0" />
              <div><p className="font-semibold">{title}</p><p className="text-sm text-slate-400 mt-1">{text}</p></div>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-6">Example: on a $3,000 trip you receive $2,550 and VoyageQueue keeps $450. Every partner and package is reviewed before going live.</p>
      </div>
      <div className="lg:col-span-6">
        {user ? (
          <div className="lux-card p-8">
            <p className="text-slate-300">You're signed in as {user.email}.</p>
            {user.role === "broker"
              ? <button data-testid="partners-go-portal" onClick={() => nav("/broker")} className="btn-gold px-6 py-3 mt-5">Open partner portal</button>
              : <p className="text-sm text-slate-500 mt-3">Partner accounts are separate from member accounts. Sign out and apply with your business email.</p>}
          </div>
        ) : (
          <form onSubmit={submit} data-testid="partner-apply-form" className="lux-card p-8 space-y-4">
            <p className="font-serif-lux text-2xl">Apply to partner</p>
            {FIELDS.map(([k, label, type]) => (
              <label key={k} className="block"><span className="text-xs text-slate-400">{label}</span>
                <input data-testid={`partner-${k}-input`} type={type || "text"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                  required={["name", "email", "password", "company", "license_number"].includes(k)} className="lux-input mt-1" /></label>
            ))}
            <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
              <input data-testid="partner-terms-checkbox" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="accent-[#38BDF8] w-4 h-4 mt-0.5" />
              I confirm my business is licensed/registered to sell travel where required, I'm the travel provider responsible for packages I list, and I agree to the 15% fee (first booking free).
            </label>
            <button data-testid="partner-apply-submit" disabled={busy || !agree} className="btn-gold w-full py-3.5">{busy ? "Submitting…" : "Submit application"}</button>
          </form>
        )}
      </div>
    </div>
  );
}
